/**
 * Sistema global de ventanas modales arrastrables y redimensionables (Draggable & Resizable Modals)
 *
 * Características:
 * 1. Arrastrable: Mover cualquier ventana modal (.modal-dialog o .modal-container) arrastrándola desde su barra de título (.modal-header).
 * 2. Redimensionable: Cambiar el tamaño arrastrando desde el tirador en la esquina inferior derecha (.modal-resize-handle) o el borde inferior/derecho.
 * 3. Maximizar/Restaurar: Doble clic en la barra de título (.modal-header) para alternar pantalla completa / tamaño previo.
 * 4. Resetear tamaño: Doble clic en el tirador de redimensión (.modal-resize-handle) para volver a las dimensiones por defecto.
 * 5. Soporte nativo para modales anidados: Al usar posicionamiento relativo con top/left sin transform, los backdrops fixed (.modal-mask / .nested-modal-mask) cubren el 100vw x 100vh del viewport sin atraparse en un containing block.
 */

let isInitialized = false;

export function initGlobalDraggableModals() {
  if (isInitialized) return;
  isInitialized = true;

  // Estado de Arrastre (Drag)
  let isDragging = false;
  let activeDialog = null;
  let activeHeader = null;
  let startMouseX = 0;
  let startMouseY = 0;
  let startTranslateX = 0;
  let startTranslateY = 0;

  // Estado de Redimensión (Resize)
  let isResizing = false;
  let activeResizeDialog = null;
  let activeResizeContent = null;
  let startResizeMouseX = 0;
  let startResizeMouseY = 0;
  let startWidth = 0;
  let startHeight = 0;

  /**
   * Obtiene los valores actuales de desplazamiento (style.left / style.top) de un elemento
   */
  function getCurrentOffset(element) {
    const left = parseFloat(element.style.left) || 0;
    const top = parseFloat(element.style.top) || 0;
    return { x: left, y: top };
  }

  // --- Manejadores de Arrastre (Drag) ---

  function handleDragStart(clientX, clientY, target) {
    const header = target.closest(".modal-header");
    if (!header) return;

    // Ignorar clics en botones, enlaces, campos de formulario o tiradores de redimensión
    if (target.closest("button, .close, a, input, select, textarea, [data-dismiss], .modal-resize-handle")) {
      return;
    }

    const dialog = header.closest(".modal-dialog, .modal-container, .modal-content");
    if (!dialog) return;

    isDragging = true;
    activeDialog = dialog;
    activeHeader = header;
    startMouseX = clientX;
    startMouseY = clientY;

    dialog.style.position = "relative";
    const currentOffset = getCurrentOffset(dialog);
    startTranslateX = currentOffset.x;
    startTranslateY = currentOffset.y;

    header.classList.add("modal-header-dragging");
    document.body.classList.add("modal-is-dragging");
  }

  function handleDragMove(clientX, clientY) {
    if (!isDragging || !activeDialog) return;

    const deltaX = clientX - startMouseX;
    const deltaY = clientY - startMouseY;

    const newX = Math.round(startTranslateX + deltaX);
    const newY = Math.round(startTranslateY + deltaY);

    activeDialog.style.left = `${newX}px`;
    activeDialog.style.top = `${newY}px`;
  }

  function handleDragEnd() {
    if (isDragging && activeHeader) {
      activeHeader.classList.remove("modal-header-dragging");
      document.body.classList.remove("modal-is-dragging");
    }
    isDragging = false;
    activeDialog = null;
    activeHeader = null;
  }

  // --- Manejadores de Redimensión (Resize) ---

  function handleResizeStart(clientX, clientY, target) {
    const handle = target.closest(".modal-resize-handle");
    let content = null;
    let dialog = null;

    if (handle) {
      content = handle.closest(".modal-content, .modal-container");
      dialog = handle.closest(".modal-dialog, .modal-container, .modal-content");
    } else {
      // Detección por proximidad a la esquina inferior derecha (últimos 20px)
      const modalEl = target.closest(".modal-content, .modal-container");
      if (modalEl && !target.closest("button, a, input, select, textarea")) {
        const rect = modalEl.getBoundingClientRect();
        const inCornerX = clientX >= rect.right - 22 && clientX <= rect.right;
        const inCornerY = clientY >= rect.bottom - 22 && clientY <= rect.bottom;
        if (inCornerX && inCornerY) {
          content = modalEl;
          dialog = modalEl.closest(".modal-dialog, .modal-container, .modal-content");
        }
      }
    }

    if (!dialog || !content) return;

    isResizing = true;
    activeResizeDialog = dialog;
    activeResizeContent = content;
    startResizeMouseX = clientX;
    startResizeMouseY = clientY;

    const dialogRect = dialog.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();

    startWidth = dialogRect.width;
    startHeight = contentRect.height;

    document.body.classList.add("modal-is-resizing");
  }

  function handleResizeMove(clientX, clientY) {
    if (!isResizing || !activeResizeDialog || !activeResizeContent) return;

    const deltaX = clientX - startResizeMouseX;
    const deltaY = clientY - startResizeMouseY;

    const minWidth = 320;
    const maxWidth = Math.round(window.innerWidth * 0.98);
    const minHeight = 180;
    const maxHeight = Math.round(window.innerHeight * 0.96);

    const newWidth = Math.round(Math.max(minWidth, Math.min(maxWidth, startWidth + deltaX)));
    const newHeight = Math.round(Math.max(minHeight, Math.min(maxHeight, startHeight + deltaY)));

    activeResizeDialog.style.width = `${newWidth}px`;
    activeResizeDialog.style.maxWidth = "none";
    activeResizeDialog.style.minWidth = "auto";

    activeResizeContent.style.height = `${newHeight}px`;
    activeResizeContent.style.maxHeight = "none";
    activeResizeContent.style.minHeight = "auto";
  }

  function handleResizeEnd() {
    if (isResizing) {
      document.body.classList.remove("modal-is-resizing");
    }
    isResizing = false;
    activeResizeDialog = null;
    activeResizeContent = null;
  }

  // --- Maximizar / Restaurar en Doble Clic ---

  function handleHeaderDoubleClick(e) {
    const header = e.target.closest(".modal-header");
    if (!header) return;
    if (e.target.closest("button, .close, a, input, select, textarea, [data-dismiss]")) return;

    const dialog = header.closest(".modal-dialog, .modal-container, .modal-content");
    const content = header.closest(".modal-content, .modal-container");
    if (!dialog) return;

    if (dialog.dataset.isMaximized === "true") {
      // Restaurar a tamaño previo
      dialog.style.width = dialog.dataset.prevWidth || "";
      dialog.style.maxWidth = dialog.dataset.prevMaxWidth || "";
      dialog.style.left = dialog.dataset.prevLeft || "";
      dialog.style.top = dialog.dataset.prevTop || "";
      if (content) {
        content.style.height = dialog.dataset.prevHeight || "";
        content.style.maxHeight = dialog.dataset.prevMaxHeight || "";
      }
      dialog.dataset.isMaximized = "false";
      dialog.classList.remove("modal-maximized");
    } else {
      // Maximizar a tamaño amplio
      dialog.dataset.prevWidth = dialog.style.width;
      dialog.dataset.prevMaxWidth = dialog.style.maxWidth;
      dialog.dataset.prevLeft = dialog.style.left;
      dialog.dataset.prevTop = dialog.style.top;
      if (content) {
        dialog.dataset.prevHeight = content.style.height;
        dialog.dataset.prevMaxHeight = content.style.maxHeight;
      }

      dialog.style.width = "96vw";
      dialog.style.maxWidth = "96vw";
      dialog.style.left = "0px";
      dialog.style.top = "0px";
      if (content) {
        content.style.height = "92vh";
        content.style.maxHeight = "92vh";
      }
      dialog.dataset.isMaximized = "true";
      dialog.classList.add("modal-maximized");
    }
  }

  function handleResizeHandleDoubleClick(e) {
    const handle = e.target.closest(".modal-resize-handle");
    if (!handle) return;

    const dialog = handle.closest(".modal-dialog, .modal-container, .modal-content");
    const content = handle.closest(".modal-content, .modal-container");

    if (dialog) {
      dialog.style.width = "";
      dialog.style.maxWidth = "";
      dialog.style.minWidth = "";
      dialog.style.left = "";
      dialog.style.top = "";
      dialog.dataset.isMaximized = "false";
    }
    if (content) {
      content.style.height = "";
      content.style.maxHeight = "";
      content.style.minHeight = "";
    }
  }

  // --- Inyección de Tiradores de Redimensión en Modales ---

  function setupResizeHandleObserver() {
    const attachHandles = () => {
      const modals = document.querySelectorAll(".modal-content, .modal-container");
      modals.forEach((modal) => {
        if (!modal.querySelector(":scope > .modal-resize-handle")) {
          const handle = document.createElement("div");
          handle.className = "modal-resize-handle";
          handle.setAttribute("title", "Arrastrar para redimensionar / Doble clic para restaurar tamaño");
          handle.innerHTML = `
            <svg viewBox="0 0 16 16" width="12" height="12" fill="currentColor">
              <path d="M14 14v-2h-2v2h2zm0-4v-2h-2v2h2zm-4 4v-2H8v2h2zm4-8V4h-2v2h2zm-4 4V8H8v2h2zm-4 4v-2H4v2h2z"/>
            </svg>
          `;
          modal.style.position = "relative";
          modal.appendChild(handle);
        }
      });
    };

    if (typeof MutationObserver !== "undefined") {
      const observer = new MutationObserver(attachHandles);
      observer.observe(document.body, { childList: true, subtree: true });
    }

    attachHandles();
  }

  // --- Event Listeners Globales ---

  // Mouse Down
  document.addEventListener("mousedown", (e) => {
    if (e.button !== 0) return; // Solo clic principal izquierdo

    // Si clicó en tirador de redimensión o esquina inferior derecha
    if (e.target.closest(".modal-resize-handle") || (e.target.closest(".modal-content, .modal-container") && !e.target.closest(".modal-header"))) {
      const handle = e.target.closest(".modal-resize-handle");
      if (handle) {
        handleResizeStart(e.clientX, e.clientY, e.target);
        return;
      }
      // Revisar si está en la esquina
      const modalEl = e.target.closest(".modal-content, .modal-container");
      if (modalEl && !e.target.closest("button, a, input, select, textarea")) {
        const rect = modalEl.getBoundingClientRect();
        if (e.clientX >= rect.right - 22 && e.clientY >= rect.bottom - 22) {
          handleResizeStart(e.clientX, e.clientY, e.target);
          return;
        }
      }
    }

    // Si clicó en el header, iniciar arrastre
    handleDragStart(e.clientX, e.clientY, e.target);
  });

  // Mouse Move
  document.addEventListener("mousemove", (e) => {
    if (isResizing) {
      e.preventDefault();
      handleResizeMove(e.clientX, e.clientY);
    } else if (isDragging) {
      e.preventDefault();
      handleDragMove(e.clientX, e.clientY);
    }
  });

  // Mouse Up
  document.addEventListener("mouseup", () => {
    if (isResizing) {
      handleResizeEnd();
    }
    if (isDragging) {
      handleDragEnd();
    }
  });

  // Touch Events (Móviles / Tablets)
  document.addEventListener(
    "touchstart",
    (e) => {
      if (e.touches && e.touches.length === 1) {
        const touch = e.touches[0];
        if (e.target.closest(".modal-resize-handle")) {
          handleResizeStart(touch.clientX, touch.clientY, e.target);
        } else {
          handleDragStart(touch.clientX, touch.clientY, e.target);
        }
      }
    },
    { passive: true }
  );

  document.addEventListener(
    "touchmove",
    (e) => {
      if (e.touches && e.touches.length === 1) {
        const touch = e.touches[0];
        if (isResizing) {
          handleResizeMove(touch.clientX, touch.clientY);
        } else if (isDragging) {
          handleDragMove(touch.clientX, touch.clientY);
        }
      }
    },
    { passive: true }
  );

  document.addEventListener("touchend", () => {
    if (isResizing) handleResizeEnd();
    if (isDragging) handleDragEnd();
  });

  // Double Click Handlers
  document.addEventListener("dblclick", (e) => {
    if (e.target.closest(".modal-resize-handle")) {
      handleResizeHandleDoubleClick(e);
    } else if (e.target.closest(".modal-header")) {
      handleHeaderDoubleClick(e);
    }
  });

  // Inicializar observador de tiradores
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      setupResizeHandleObserver();
    });
  } else {
    setupResizeHandleObserver();
  }
}
